import { useState } from 'react';
import {
  ArrayInput,
  BooleanInput,
  Create,
  DateInput,
  email,
  FormTab,
  HttpError,
  ImageField,
  ImageInput,
  NumberInput,
  PasswordInput,
  required,
  SelectInput,
  SimpleFormIterator,
  TabbedForm,
  TextInput,
  useNotify,
} from 'react-admin';
import { API_BASE_URL_ADMIN } from 'shared/config';
import { API_URL } from 'shared/constants/api-url';
import { LABELS } from 'shared/constants/labels';
import { VARS } from 'shared/constants/vars';
import { httpClient, getErrors } from 'shared/services/http-client';
import { uploadFile } from 'shared/utils/upload-file';
import { Alert, Typography } from '@mui/material';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';

const promocodeExpireDateDefaultValue = new Date(
  new Date().getFullYear(),
  new Date().getMonth() + 1,
).toISOString();

export const CompaniesCreate = () => {
  const notify = useNotify();
  const [isReadOnly, setIsReadOnly] = useState(true);

  const [error, setError] = useState<string | undefined>();
  const [errors, setErrors ] = useState<string[]>([]);

  const handleSubmit = async ({ promocodeExpireDate, ...data }: any) => {
    setError(undefined);
    setErrors([]);

    if (data.user.avatarKey?.rawFile) {
      const uploadedFile = await uploadFile(data.user.avatarKey?.rawFile);

      if (uploadedFile.key) {
        data.user.avatarKey = uploadedFile.key;
        const expireDate = new Date(promocodeExpireDate);
        expireDate.setHours(15);

        const filters = data.company?.therapistsFilters ?? {};

        data.company.therapistsFilters = {
          ...filters,
          priceMin: filters.priceMin === '' ? null : filters.priceMin,
          priceMax: filters.priceMax === '' ? null : filters.priceMax,
        };

        try {
          await httpClient(`${API_BASE_URL_ADMIN}${API_URL.createCompany}`, {
            method: 'POST',
            body: JSON.stringify({
              ...data,
              promocodeExpireDate: expireDate.toISOString(),
            }),
          });

          notify('Новая компания создана', { type: 'success' });
        } catch (httpError) {
          if (httpError instanceof HttpError) {
            const [error, errors] = getErrors(httpError);

            setError(error);
            setErrors(errors);
          }

          notify('Что-то пошло не так...', { type: 'error' });
        }
      }
    }
  };

  return (
    <Create title="New company">
      <>
        {error && (
            <Alert severity="error" variant="filled" sx={{ m: 2 }}>
              <>
                {error}{errors.length > 0 && <>:</>}
                {errors.length > 0 && (
                    <List>
                      {errors.map((error, index) => (
                          <ListItem key={`error-${index}`}>{error}</ListItem>
                      ))}
                    </List>
                )}
              </>
            </Alert>
        )}

        <TabbedForm
            onSubmit={handleSubmit}
            onFocus={() => isReadOnly && setIsReadOnly(false)}
        >
          <FormTab label="Пользователь">
            <TextInput
                label={LABELS.firstname}
                source="user.firstname"
                validate={[required()]}
                fullWidth
                inputProps={{ readOnly: isReadOnly }}
            />
            <TextInput
                label={LABELS.lastname}
                source="user.lastname"
                validate={[required()]}
                fullWidth
                inputProps={{ readOnly: isReadOnly }}
            />
            <TextInput
                label={LABELS.email}
                source="user.email"
                validate={[required(), email()]}
                fullWidth
                inputProps={{ readOnly: isReadOnly }}
            />
            <TextInput
                label="Request id"
                source="requestId"
                validate={[required()]}
                fullWidth
                inputProps={{ readOnly: isReadOnly }}
            />
            <PasswordInput
                label={LABELS.password}
                source="user.password"
                validate={[required()]}
                fullWidth
                inputProps={{ readOnly: isReadOnly }}
            />
            <ImageInput
                label={LABELS.avatarKey}
                source="user.avatarKey"
                validate={[required()]}
                accept="image/*"
            >
              <ImageField source="src" title="title" />
            </ImageInput>
          </FormTab>
          <FormTab label="Компания">
            <TextInput
                label={LABELS.companyName}
                source="company.name"
                validate={[required()]}
                fullWidth
            />
            <SelectInput
                label={LABELS.companyType}
                source="company.type"
                validate={[required()]}
                fullWidth
                choices={VARS.companyTypesChoices}
            />
            <Typography variant="h6">Дополнительные фильтры каталога терапевтов</Typography>
            <TextInput
                label={LABELS.priceMin}
                source="company.therapistsFilters.priceMin"
                fullWidth
            />
            <TextInput
                label={LABELS.priceMax}
                source="company.therapistsFilters.priceMax"
                fullWidth
            />
            <BooleanInput
                label={LABELS.onlyCoaches}
                source="company.therapistsFilters.coaches"
            />
          </FormTab>
          <FormTab label="Промокоды">
            <ArrayInput source="promocodes" defaultValue={[]}>
              <SimpleFormIterator>
                <SelectInput
                    label={LABELS.promocodeAmount}
                    source="amount"
                    validate={[required()]}
                    fullWidth
                    choices={VARS.promocodesChoices}
                />
                <NumberInput
                    label={LABELS.promocodeQuantity}
                    source="quantity"
                    fullWidth
                />
              </SimpleFormIterator>
            </ArrayInput>
            <DateInput
                label={LABELS.promocodeExpireDate}
                source="promocodeExpireDate"
                defaultValue={promocodeExpireDateDefaultValue}
                fullWidth
            />
          </FormTab>
        </TabbedForm>
      </>
    </Create>
  );
};
